import { useCallback, useRef, useState } from "react";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import providers from "lib/providers";
import { stripMetaPrefix } from "lib/messageMetadata";

interface SearchState {
  results: Message[];
  currentIndex: number;
  query: string;
  highlightId: string | undefined;
  allLoaded: boolean;
}

export default function useMessageSearch(threadId: string | undefined) {
  const [state, setState] = useState<SearchState>({
    results: [],
    currentIndex: 0,
    query: "",
    highlightId: undefined,
    allLoaded: false,
  });

  const allMessagesRef = useRef<Message[]>([]);
  const loadingRef = useRef(false);

  // Load all messages for the thread (paginate through all pages)
  const loadAllMessages = useCallback(
    async (forceReload = false): Promise<Message[]> => {
      if (!threadId || loadingRef.current) return allMessagesRef.current;
      if (!forceReload && allMessagesRef.current.length > 0) return allMessagesRef.current;

      loadingRef.current = true;
      const all: Message[] = [];
      let page = 0;
      let hasMore = true;

      while (hasMore) {
        try {
          const response = await providers.MessagesProvider.find({
            data: { threadId, sort: "TIME,DESC", page, size: 50 },
          });
          all.push(...response.result);
          hasMore = response.hasNextPage;
          page++;
        } catch {
          break;
        }
      }

      allMessagesRef.current = all;
      loadingRef.current = false;
      return all;
    },
    [threadId]
  );

  // Search through messages — first search loaded, then load all if needed
  const search = useCallback(
    async (query: string, loadedMessages: Message[]) => {
      if (!query.trim()) {
        setState({ results: [], currentIndex: 0, query: "", highlightId: undefined, allLoaded: false });
        return;
      }

      const q = query.toLowerCase();
      const searchIn = (msgs: Message[]) =>
        msgs.filter((m) => stripMetaPrefix(m.text).toLowerCase().includes(q)).sort((a, b) => Number(b.id) - Number(a.id));

      // First: search in already-loaded messages (instant)
      let results = searchIn(loadedMessages);

      if (results.length > 0) {
        setState({
          results,
          currentIndex: 0,
          query,
          highlightId: results[0]?.id,
          allLoaded: false,
        });
      }

      // Then: load all messages and search comprehensively (force reload to catch new messages)
      const allMsgs = await loadAllMessages(true);
      results = searchIn(allMsgs);

      setState({
        results,
        currentIndex: 0,
        query,
        highlightId: results[0]?.id,
        allLoaded: true,
      });
    },
    [loadAllMessages]
  );

  // Navigate between results
  const navigate = useCallback((direction: "prev" | "next") => {
    setState((prev) => {
      if (prev.results.length === 0) return prev;
      let idx = prev.currentIndex;
      if (direction === "next") {
        idx = (idx + 1) % prev.results.length;
      } else {
        idx = (idx - 1 + prev.results.length) % prev.results.length;
      }
      return {
        ...prev,
        currentIndex: idx,
        highlightId: prev.results[idx]?.id,
      };
    });
  }, []);

  // Clear search and reset
  const clear = useCallback(() => {
    setState({ results: [], currentIndex: 0, query: "", highlightId: undefined, allLoaded: false });
  }, []);

  // Reset when thread changes
  const reset = useCallback(() => {
    allMessagesRef.current = [];
    loadingRef.current = false;
    clear();
  }, [clear]);

  return {
    searchResults: state.results,
    searchIndex: state.currentIndex,
    searchQuery: state.query,
    searchHighlightId: state.highlightId,
    search,
    navigate,
    clearSearch: clear,
    resetSearch: reset,
  };
}
